import { Input, Label, Textarea } from '@wally-reseller/ui/input';
import type { ReactNode } from 'react';
import { useT } from '@/lib/i18n';

export interface RegistrationDraft {
  legalName: string;
  since: string;
  description: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

export const emptyDraft: RegistrationDraft = {
  legalName: '',
  since: '',
  description: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
};

export function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <div className="text-xs text-danger">{error}</div>
      ) : (
        hint && <div className="text-xs text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}

/** The fields a partner fills in and may correct while Wally has not decided (no company number). */
export function RegistrationFields({
  draft,
  onChange,
}: {
  draft: RegistrationDraft;
  onChange: (draft: RegistrationDraft) => void;
}) {
  const t = useT();
  const set = (key: keyof RegistrationDraft) => (e: { target: { value: string } }) =>
    onChange({ ...draft, [key]: e.target.value });
  return (
    <>
      <Field id="legalName" label={t('portal.field.legalName')}>
        <Input
          id="legalName"
          required
          minLength={2}
          value={draft.legalName}
          onChange={set('legalName')}
        />
      </Field>
      <Field id="since" label={t('portal.field.since')} hint={t('portal.field.since.hint')}>
        <Input
          id="since"
          required
          className="max-w-48"
          value={draft.since}
          onChange={set('since')}
        />
      </Field>
      <Field
        id="description"
        label={t('portal.field.description')}
        hint={t('portal.field.description.hint')}
      >
        <Textarea
          id="description"
          required
          minLength={10}
          rows={4}
          value={draft.description}
          onChange={set('description')}
        />
      </Field>
      <fieldset className="space-y-3 rounded-md border border-border p-3">
        <legend className="px-1 text-sm font-medium">{t('portal.field.contact')}</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field id="contactName" label={t('portal.field.contactName')}>
            <Input id="contactName" value={draft.contactName} onChange={set('contactName')} />
          </Field>
          <Field id="contactEmail" label={t('portal.field.contactEmail')}>
            <Input
              id="contactEmail"
              type="email"
              value={draft.contactEmail}
              onChange={set('contactEmail')}
            />
          </Field>
          <Field id="contactPhone" label={t('portal.field.contactPhone')}>
            <Input
              id="contactPhone"
              type="tel"
              value={draft.contactPhone}
              onChange={set('contactPhone')}
            />
          </Field>
        </div>
      </fieldset>
    </>
  );
}

export function draftToInput(d: RegistrationDraft) {
  return {
    legalName: d.legalName,
    relationship: { since: d.since, description: d.description },
    contact: { name: d.contactName, email: d.contactEmail, phone: d.contactPhone },
  };
}
